import { useMutation } from '@tanstack/react-query';
import { Bell, Lock } from 'lucide-react';
import { api } from '../../../api/client';
import { useAuthStore } from '../../../store/auth';
import { toast } from '../../../lib/toast';
import { upgradeHintFor } from '../../../lib/features';

function hasAutoPilotFeature(user) {
  const ap = user?.entitlements?.autoPilot;
  if (!ap?.active) return false;
  if (!ap.expiresAt) return true;
  return new Date(ap.expiresAt).getTime() > Date.now();
}

export default function NotificationsSection() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const hasAutoPilot = hasAutoPilotFeature(user);
  const digestEnabled = user?.weeklyDigestEnabled !== false;

  const digestMutation = useMutation({
    mutationFn: (enabled) =>
      api.put('/api/me/profile', { weeklyDigestEnabled: enabled }).then((r) => r.data),
    onSuccess: (data) => {
      if (data?.user) setUser(data.user);
      toast.success('Notification preference saved');
    },
    onError: (err) => toast.error(err, 'Could not save preference'),
  });

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Bell className="w-4 h-4" />
          </span>
          Notifications
        </div>
      </div>

      <p className="text-sm text-ink-500 mb-4">
        Email preferences that apply across all your domains. Per-domain alerts (score
        drops, bot-activity surges) live on each domain&apos;s Settings tab.
      </p>

      {/* Weekly digest */}
      <div className="rounded-lg border border-gray-100 bg-gray-50/40 p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="flex items-center gap-2 font-medium text-ink-800">
              Weekly AutoPilot digest
              {!hasAutoPilot && (
                <span className="pill bg-gray-100 text-gray-500 text-[10px]">
                  <Lock className="w-3 h-3 inline-block -mt-0.5" /> AutoPilot
                </span>
              )}
            </div>
            <p className="text-xs text-ink-500 mt-1">
              Sunday recap of audits, score changes, AI referrals, and top crawler bots
              across all your domains.
            </p>
          </div>
        </div>

        {hasAutoPilot ? (
          <button
            type="button"
            className={`w-full rounded-lg px-3 py-2 text-left text-sm transition border ${
              digestEnabled
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : 'bg-white border-gray-200 text-ink-700'
            }`}
            disabled={digestMutation.isPending}
            onClick={() => digestMutation.mutate(!digestEnabled)}
          >
            <span className="inline-flex items-center gap-2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  digestEnabled ? 'bg-emerald-500' : 'bg-gray-300'
                }`}
              />
              {digestEnabled ? 'Enabled — sent Sunday at 12:00 UTC' : 'Paused'}
            </span>
          </button>
        ) : (
          <p className="text-xs text-ink-400">
            Upgrade to {upgradeHintFor('autoPilotScheduler')} to receive the weekly digest.
          </p>
        )}
      </div>
    </div>
  );
}
